import styled, { keyframes } from "styled-components";

/* ═══════════════════════════════════════════════════════════════════════
   ConfirmationModal — ICON-LED CENTERED COMPOSITION
   Big circular variant-colored icon at the top, centered title, centered
   message, two-button row at the bottom. Shell matches the water system.
   ═══════════════════════════════════════════════════════════════════════ */

const panelFloat = keyframes`
  0%, 100% { filter: brightness(1); }
  50%      { filter: brightness(1.02); }
`;

const iconHalo = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 0.55;
  }
  50% {
    transform: scale(1.08);
    opacity: 0.85;
  }
`;

const buttonShimmer = keyframes`
  0%   { left: -100%; }
  100% { left: 150%; }
`;

/* Variant tokens — used by IconWrapper + ConfirmButton */
const VARIANT = {
  danger: {
    iconBg: `radial-gradient(circle at 30% 30%, rgba(255, 220, 228, 0.95) 0%, rgba(255, 170, 186, 0.88) 50%, rgba(255, 120, 143, 0.82) 100%)`,
    iconBorder: "rgba(255, 120, 143, 0.65)",
    iconShadow: "0 10px 28px rgba(211, 71, 99, 0.35)",
    iconGlow: "rgba(255, 120, 143, 0.55)",
    iconColor: "#d34763",
    btnGradient:
      "linear-gradient(180deg, #ff7a88 0%, #ff4757 55%, #d6263a 100%)",
    btnShadow: "0 14px 28px rgba(255, 71, 87, 0.38)",
    btnShadowHover: "0 18px 34px rgba(255, 71, 87, 0.5)",
    btnInnerDark: "rgba(180, 30, 54, 0.3)",
  },
  warning: {
    iconBg: `radial-gradient(circle at 30% 30%, rgba(255, 244, 214, 0.95) 0%, rgba(255, 220, 150, 0.9) 50%, rgba(255, 190, 80, 0.85) 100%)`,
    iconBorder: "rgba(245, 158, 11, 0.55)",
    iconShadow: "0 10px 28px rgba(217, 119, 6, 0.32)",
    iconGlow: "rgba(255, 190, 80, 0.6)",
    iconColor: "#b45309",
    btnGradient:
      "linear-gradient(180deg, #fbbf24 0%, #f59e0b 55%, #d97706 100%)",
    btnShadow: "0 14px 28px rgba(217, 119, 6, 0.36)",
    btnShadowHover: "0 18px 34px rgba(217, 119, 6, 0.48)",
    btnInnerDark: "rgba(146, 64, 14, 0.3)",
  },
  success: {
    iconBg: `radial-gradient(circle at 30% 30%, rgba(220, 255, 240, 0.95) 0%, rgba(172, 232, 206, 0.9) 50%, rgba(94, 226, 164, 0.85) 100%)`,
    iconBorder: "rgba(16, 185, 129, 0.55)",
    iconShadow: "0 10px 28px rgba(13, 144, 96, 0.32)",
    iconGlow: "rgba(94, 226, 164, 0.55)",
    iconColor: "#0d9060",
    btnGradient:
      "linear-gradient(180deg, #5ee2a4 0%, #10b981 55%, #0d9060 100%)",
    btnShadow: "0 14px 28px rgba(16, 185, 129, 0.36)",
    btnShadowHover: "0 18px 34px rgba(16, 185, 129, 0.48)",
    btnInnerDark: "rgba(6, 95, 70, 0.3)",
  },
  primary: {
    iconBg: `radial-gradient(circle at 30% 30%, rgba(var(--glass-pale-rgb), 0.95) 0%, rgba(170, 220, 255, 0.9) 50%, rgba(var(--color-brand-400-rgb), 0.82) 100%)`,
    iconBorder: "rgba(var(--color-brand-400-rgb), 0.55)",
    iconShadow: "0 10px 28px rgba(var(--color-brand-600-rgb), 0.34)",
    iconGlow: "rgba(var(--color-brand-400-rgb), 0.6)",
    iconColor: "var(--color-brand-600)",
    btnGradient:
      "linear-gradient(180deg, var(--color-brand-400) 0%, var(--color-brand-600) 55%, var(--color-brand-700) 100%)",
    btnShadow: "0 14px 28px rgba(var(--color-brand-600-rgb), 0.38)",
    btnShadowHover: "0 18px 34px rgba(var(--color-brand-600-rgb), 0.5)",
    btnInnerDark: "rgba(var(--color-brand-700-rgb), 0.3)",
  },
};
const v = (p) => VARIANT[p.$variant] || VARIANT.danger;

// ─── Shell ─────────────────────────────────────────────────────────────

export const ModalContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: min(92dvw, 420px);
  max-height: 90dvh;
  overflow: hidden;
  border-radius: 28px;
  border: 1px solid var(--glass-edge);
  background:
    radial-gradient(
      120% 60% at 30% -10%,
      rgba(var(--glass-white-rgb), 0.96) 0%,
      rgba(var(--glass-pale-rgb), 0.9) 55%,
      rgba(var(--glass-pale-rgb), 0.82) 100%
    ),
    linear-gradient(
      180deg,
      rgba(var(--glass-white-rgb), 0.94) 0%,
      rgba(var(--glass-pale-rgb), 0.9) 100%
    );
  backdrop-filter: blur(28px) saturate(180%);
  -webkit-backdrop-filter: blur(28px) saturate(180%);
  box-shadow:
    0 32px 64px -18px rgba(var(--color-brand-500-rgb), 0.4),
    0 12px 28px rgba(var(--color-brand-400-rgb), 0.22),
    inset 0 2px 0 rgba(255, 255, 255, 0.95),
    inset 0 -10px 22px rgba(var(--color-brand-200-rgb), 0.26),
    inset 0 0 0 1px rgba(255, 255, 255, 0.45);
  animation: ${panelFloat} 5.5s ease-in-out infinite;

  [data-theme="dark"] & {
    border-color: transparent;
    box-shadow:
      0 28px 56px -18px rgba(0, 0, 0, 0.6),
      0 10px 24px rgba(0, 0, 0, 0.4),
      inset 0 -10px 22px rgba(var(--color-brand-700-rgb), 0.18);
  }

  /* Top specular sheen */
  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 32px;
    right: 32px;
    height: 18px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.9) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(2px);
    pointer-events: none;
    z-index: 10;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  @media (max-width: 475px) {
    border-radius: 24px;
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

export const ModalContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 32px 28px 26px;
  gap: 0;

  @media (max-width: 475px) {
    padding: 28px 22px 22px;
  }
`;

/* Row retained for JS compat — now just a hidden wrapper for close button */
export const Row = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 12;
`;

/* Close — frosted water circle, absolute top-right */
export const CloseButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 1px solid rgba(var(--glass-white-rgb), 0.7);
  background: radial-gradient(
    130% 100% at 30% 15%,
    rgba(var(--glass-white-rgb), 0.85) 0%,
    rgba(var(--glass-white-rgb), 0.58) 45%,
    rgba(var(--glass-pale-rgb), 0.42) 100%
  );
  backdrop-filter: blur(16px) saturate(180%);
  -webkit-backdrop-filter: blur(16px) saturate(180%);
  box-shadow:
    0 6px 14px rgba(var(--color-brand-500-rgb), 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.95),
    inset 0 -3px 8px rgba(var(--color-brand-200-rgb), 0.2);
  cursor: pointer;
  outline: none;
  overflow: hidden;
  color: var(--color-brand-700);
  transition:
    transform 200ms var(--ease-snappy),
    border-color 200ms var(--ease-snappy);

  [data-theme="dark"] & {
    border-color: transparent;
    background: var(--color-surface-3);
    box-shadow:
      0 4px 10px rgba(0, 0, 0, 0.45),
      inset 0 -2px 6px rgba(var(--color-brand-700-rgb), 0.16);
  }

  [data-theme="dark"] & svg path {
    stroke: var(--color-text-strong);
  }

  &::before {
    content: "";
    position: absolute;
    top: 3px;
    left: 5px;
    width: 14px;
    height: 8px;
    border-radius: 50%;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.88) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  svg {
    width: 14px;
    height: 14px;
    position: relative;
    z-index: 1;
    path {
      stroke: var(--color-brand-700);
    }
  }

  &:hover {
    transform: translateY(-1px) scale(1.05);
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

// ─── Hero icon ─────────────────────────────────────────────────────────

export const IconWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  margin-bottom: 16px;
  border: 1.5px solid ${(p) => v(p).iconBorder};
  background: ${(p) => v(p).iconBg};
  box-shadow:
    ${(p) => v(p).iconShadow},
    inset 0 1.5px 0 rgba(255, 255, 255, 0.9),
    inset 0 -3px 8px rgba(255, 255, 255, 0.3);

  /* Soft halo pulse behind the icon */
  &::before {
    content: "";
    position: absolute;
    inset: -12px;
    border-radius: 50%;
    background: radial-gradient(
      circle,
      ${(p) => v(p).iconGlow} 0%,
      rgba(255, 255, 255, 0) 65%
    );
    filter: blur(4px);
    z-index: -1;
    animation: ${iconHalo} 3.2s ease-in-out infinite;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  /* Top specular reflection */
  &::after {
    content: "";
    position: absolute;
    top: 6px;
    left: 18px;
    right: 18px;
    height: 10px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.85) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    pointer-events: none;
  }

  svg {
    position: relative;
    z-index: 1;
    width: 34px;
    height: 34px;
    color: ${(p) => v(p).iconColor};
    path {
      stroke: ${(p) => v(p).iconColor};
      /* fill: ${(p) => v(p).iconColor}; */
    }
  }

  [data-theme="dark"] & {
    border-color: transparent;
    box-shadow:
      ${(p) => v(p).iconShadow},
      0 8px 18px rgba(0, 0, 0, 0.45);
  }

  [data-theme="dark"] &::after {
    opacity: 0.4;
  }
`;

// ─── Text ─────────────────────────────────────────────────────────────

export const ModalTitle = styled.h2`
  margin: 0 0 10px;
  font-family: var(--font-family-primary);
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: normal;

  /* Light-water brand gradient that flips cleanly in dark theme thanks
     to the text-strong/text-body tokens. */
  background: linear-gradient(
    0deg,
    var(--color-text-strong) 0%,
    var(--color-text-body) 45%,
    var(--color-brand-600) 100%
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  filter: drop-shadow(0 1px 2px rgba(var(--color-brand-600-rgb), 0.14));
`;

export const ModalMessage = styled.p`
  margin: 0 0 28px;
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
  letter-spacing: -0.005em;
  max-width: 320px;

  [data-theme="dark"] & {
    color: rgba(255, 255, 255, 0.78);
  }
`;

// ─── Buttons ───────────────────────────────────────────────────────────

export const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
`;

/* Cancel — frosted light water pill (secondary) */
export const CancelButton = styled.button`
  position: relative;
  display: flex;
  flex: 1;
  min-height: 48px;
  padding: 0 20px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 999px;
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.26);
  background: radial-gradient(
    120% 100% at 20% 0%,
    rgba(var(--glass-white-rgb), 0.88) 0%,
    rgba(var(--glass-pale-rgb), 0.62) 55%,
    rgba(var(--glass-pale-rgb), 0.48) 100%
  );
  backdrop-filter: blur(16px) saturate(180%);
  -webkit-backdrop-filter: blur(16px) saturate(180%);
  box-shadow:
    0 6px 14px rgba(var(--color-brand-500-rgb), 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.92);
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.005em;
  cursor: pointer;
  outline: none;
  overflow: hidden;
  transition:
    transform 200ms var(--ease-snappy),
    border-color 200ms var(--ease-snappy);

  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 14px;
    right: 14px;
    height: 8px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.78) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1.2px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    border-color: rgba(var(--color-brand-400-rgb), 0.55);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  [data-theme="dark"] & {
    border-color: transparent;
    background: var(--color-surface-3);
    box-shadow:
      0 4px 10px rgba(0, 0, 0, 0.45),
      inset 0 -2px 6px rgba(var(--color-brand-700-rgb), 0.16);
    color: var(--color-brand-300);

    &:hover:not(:disabled) {
      background: rgba(var(--color-brand-700-rgb), 0.32);
    }
  }
`;

/* Confirm — variant-colored primary pill with shimmer */
export const ConfirmButton = styled.button`
  position: relative;
  display: flex;
  flex: 1;
  min-height: 48px;
  padding: 0 20px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 999px;
  border: 1px solid rgba(var(--glass-white-rgb), 0.72);
  background: ${(p) => v(p).btnGradient};
  box-shadow:
    ${(p) => v(p).btnShadow},
    inset 0 1px 0 rgba(255, 255, 255, 0.55),
    inset 0 -3px 6px ${(p) => v(p).btnInnerDark};
  color: var(--glass-text-on-pill);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 800;
  letter-spacing: -0.005em;
  text-shadow: 0 1px 1px rgba(var(--color-brand-900-rgb), 0.28);
  cursor: pointer;
  outline: none;
  overflow: hidden;
  transition:
    transform 200ms var(--ease-snappy),
    filter 200ms var(--ease-snappy),
    box-shadow 200ms var(--ease-snappy),
    opacity 200ms var(--ease-snappy);

  /* Top sheen */
  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 16px;
    right: 16px;
    height: 9px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.72) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1.5px);
    pointer-events: none;
    z-index: 1;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  /* Moving shimmer */
  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 45%;
    height: 100%;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.38) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    animation: ${buttonShimmer} 4s infinite;
    pointer-events: none;
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    filter: brightness(1.06);
    box-shadow:
      ${(p) => v(p).btnShadowHover},
      inset 0 1px 0 rgba(255, 255, 255, 0.7),
      inset 0 -3px 8px ${(p) => v(p).btnInnerDark};
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  l-bouncy {
    position: relative;
    z-index: 2;
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;
